import React from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import GuideLayout, { GuidePhoto } from './GuideLayout';
import photoWreath from '../../../img/guide-kwiaty-wieniec.webp';
import photoLilies from '../../../img/guide-kwiaty-lilie.webp';
import photoChrysanthemums from '../../../img/guide-kwiaty-chryzantemy.webp';
import photoCemetery from '../../../img/guide-kwiaty-cmentarz.webp';

// Poradnik pod frazy wyszukiwane przez rodziny i kwiaciarnie: „jakie kwiaty na cmentarz”, „kwiaty na pogrzeb”,
// „wieniec pogrzebowy”, „wiązanka pogrzebowa”, „symbolika kwiatów pogrzebowych”, „kwiaty na grób”.
// Prowadzi do poradników o szarfach i generatora szarf.

// Zdjęcia z Unsplash (Unsplash License - darmowe użycie komercyjne, autor podany dobrowolnie w podpisie).
// Pobrane jako WebP 1200×700; źródło każdego zdjęcia w polu `source`.
const PHOTOS = {
    wreath: {
        src: photoWreath,
        alt: 'Wieniec z białych i różowych kwiatów oraz zieleni, przewiązany wstążkami',
        caption: 'Wieniec z kwiatów i zieleni',
        author: 'DEAD GOOD LEGACIES',
        source: 'https://unsplash.com/photos/a-floral-wreath-with-ribbons-rests-on-a-textured-surface-o9yrVGx81UE',
    },
    lilies: {
        src: photoLilies,
        alt: 'Białe lilie na ciemnym tle',
        caption: 'Białe lilie - klasyczny kwiat pogrzebowy',
        author: 'Jerry Wang',
        source: 'https://unsplash.com/photos/white-lilies-bloom-in-a-glass-vase-lunbqFx8SkQ',
    },
    chrysanthemums: {
        src: photoChrysanthemums,
        alt: 'Pomarańczowe chryzantemy z bliska',
        caption: 'Chryzantemy - w Polsce kwiat pamięci i Wszystkich Świętych',
        author: 'Kat',
        source: 'https://unsplash.com/photos/orange-chrysanthemums-bloom-beautifully-in-a-garden-TGe_zgkUoq4',
    },
    cemetery: {
        src: photoCemetery,
        alt: 'Setki zapalonych zniczy na cmentarzu Srebrzysko w Gdańsku',
        caption: 'Znicze na cmentarzu Srebrzysko w Gdańsku',
        author: 'Ludomił Sawicki',
        source: 'https://unsplash.com/photos/a-large-group-of-colorful-lit-candles-sitting-on-top-of-a-table-r5K_apW4R5o',
    },
};

const ARRANGEMENTS = [
    {
        title: 'Wieniec pogrzebowy',
        text: 'Okrągła kompozycja na stelażu - symbol wieczności i nieskończonego życia. Zwykle od najbliższej rodziny, zakładu pracy lub organizacji. Prawie zawsze z szarfą.',
    },
    {
        title: 'Wiązanka pogrzebowa',
        text: 'Mniejsza, podłużna kompozycja, którą składa się na trumnie lub przy grobie. Najczęstszy wybór dalszej rodziny, przyjaciół i sąsiadów.',
    },
    {
        title: 'Kosz i stroik',
        text: 'Kosz z kwiatami lub stroik z zielenią i szyszkami. Sprawdza się, gdy kompozycja ma dłużej stać na grobie - zwłaszcza jesienią i zimą.',
    },
    {
        title: 'Pojedyncze kwiaty',
        text: 'Jedna róża lub kilka kwiatów od siebie - skromny, osobisty gest. Zgodnie z tradycją zmarłym zanosi się parzystą liczbę kwiatów.',
    },
];

const FLOWERS = [
    { title: 'Lilia', text: 'Biała lilia to symbol niewinności i duszy, która odchodzi w spokoju. Jeden z najbardziej klasycznych kwiatów pogrzebowych.' },
    { title: 'Róża', text: 'Czerwona - miłość i bliskość, najczęściej od męża, żony, dzieci. Biała - szacunek i pamięć. Pasuje do każdej kompozycji.' },
    { title: 'Chryzantema', text: 'W Polsce kwiat pamięci i Wszystkich Świętych. Bardzo trwała - dobrze znosi chłód, dlatego tak często trafia na groby jesienią.' },
    { title: 'Goździk', text: 'Symbol pamięci i szacunku. Trwały i niedrogi, dlatego często wypełnia wieńce i większe wiązanki.' },
    { title: 'Kalia', text: 'Elegancka, smukła i szlachetna. Wybierana do nowoczesnych, minimalistycznych kompozycji.' },
    { title: 'Gerbera i eustoma', text: 'Jasne, delikatne kwiaty - często w kompozycjach dla osób młodych albo gdy rodzina chce podkreślić radość życia zmarłego.' },
    { title: 'Storczyk', text: 'Wyraz głębokiej, trwałej miłości. Szlachetny akcent w wiązankach od najbliższych.' },
    { title: 'Zieleń iglasta i bluszcz', text: 'Jodła, tuja czy świerk to symbol życia wiecznego, a bluszcz - wierności. Stanowią tło i podstawę wielu wieńców.' },
];

const COLORS = [
    { title: 'Biel i kremowy', text: 'Niewinność, spokój, czystość. Szczególnie przy pożegnaniu dzieci i młodych osób, ale pasują zawsze.' },
    { title: 'Czerwień i bordo', text: 'Miłość i pamięć. Zwykle od najbliższych: męża, żony, dzieci, rodziców.' },
    { title: 'Fiolet', text: 'Tradycyjny kolor żałoby i zadumy. Dobrze łączy się z bielą i zielenią.' },
    { title: 'Pastele', text: 'Róże, brzoskwinie, jasne błękity - łagodne pożegnanie, często przy młodszych osobach lub z prośby rodziny.' },
];

const SEASONS = [
    { title: 'Wiosna', text: 'Bratki, stokrotki, niezapominajki i cebulowe w doniczkach - szybko ożywiają grób po zimie.' },
    { title: 'Lato', text: 'Begonie, pelargonie, aksamitki i surfinie. Warto wybierać rośliny odporne na słońce i rzadsze podlewanie.' },
    { title: 'Jesień', text: 'Chryzantemy w doniczkach i wrzosy - klasyka okolic Wszystkich Świętych, trwała nawet przy przymrozkach.' },
    { title: 'Zima', text: 'Stroiki z jedliny, szyszek i suszu. Żywe kwiaty szybko marzną, więc zieleń iglasta sprawdza się najlepiej.' },
];

const TIPS = [
    { title: 'Zapytaj o życzenie rodziny', text: 'Czasem rodzina prosi o datek na cel charytatywny zamiast kwiatów albo o konkretne kolory. Informacja bywa na nekrologu.' },
    { title: 'Zamów z wyprzedzeniem', text: 'Wieniec z szarfą najlepiej zamówić dzień lub dwa przed pogrzebem - kwiaciarnia musi przygotować kompozycję i wydrukować szarfę.' },
    { title: 'Sprawdź napis na szarfie', text: 'Imiona i formy grzecznościowe przeczytaj dwa razy. Gotowe teksty znajdziesz w poradniku o napisach na szarfy.' },
    { title: 'Na grób - rośliny w doniczkach', text: 'Kwiaty cięte szybko więdną. Do odwiedzin na cmentarzu lepiej sprawdzają się rośliny doniczkowe dopasowane do pory roku.' },
    { title: 'Sztuczne kwiaty z umiarem', text: 'Są trwałe, ale na słońcu płowieją. Gdy zniszczone, wyrzuć je do odpowiedniego pojemnika na cmentarzu.' },
];

const FAQ = [
    {
        question: 'Jakie kwiaty na cmentarz?',
        answer: 'Na grób najlepiej wybierać rośliny doniczkowe dopasowane do pory roku: wiosną bratki, latem begonie i pelargonie, jesienią chryzantemy i wrzosy, zimą stroiki z jedliny. Na pogrzeb - wieniec lub wiązankę z białych lilii, róż, chryzantem albo goździków.',
    },
    {
        question: 'Jakie kwiaty na pogrzeb wypada kupić?',
        answer: 'Najbardziej uniwersalne są białe lilie, róże (białe lub czerwone), chryzantemy i goździki. Najbliższa rodzina zwykle wybiera wieniec z szarfą, dalsza rodzina i znajomi - wiązankę.',
    },
    {
        question: 'Czym różni się wieniec od wiązanki pogrzebowej?',
        answer: 'Wieniec ma okrągły kształt (symbol wieczności), jest większy i zwykle pochodzi od najbliższych lub od zakładu pracy. Wiązanka jest mniejsza i podłużna - to najczęstszy wybór dalszej rodziny i znajomych.',
    },
    {
        question: 'Parzysta czy nieparzysta liczba kwiatów na grób?',
        answer: 'Zgodnie z polską tradycją zmarłym zanosi się parzystą liczbę kwiatów, a żywym - nieparzystą. Przy dużych kompozycjach, jak wieniec czy wiązanka, liczby kwiatów się nie liczy.',
    },
    {
        question: 'Co napisać na szarfie do wieńca?',
        answer: 'Zwykle dwie szarfy: dla kogo jest wieniec (np. „Ukochanej Mamie”) i od kogo („Córka z rodziną”). Gotowe teksty dla mamy, taty, babci, przyjaciela i od współpracowników znajdziesz w poradniku o napisach na szarfy.',
    },
];

export default function KwiatyNaCmentarz() {
    return (
        <GuideLayout
            path='/jakie-kwiaty-na-cmentarz'
            title='Jakie kwiaty na cmentarz i na pogrzeb? Wieńce, wiązanki i symbolika | Nekrodruk.pl'
            description='Jakie kwiaty na cmentarz i na pogrzeb? Wieniec czy wiązanka, znaczenie lilii, róż, chryzantem i goździków, kolory kwiatów pogrzebowych oraz kwiaty na grób na każdą porę roku. Praktyczny poradnik.'
            keywords='jakie kwiaty na cmentarz, kwiaty na pogrzeb, kwiaty na grób, wieniec pogrzebowy, wiązanka pogrzebowa, symbolika kwiatów pogrzebowych, kwiaty pogrzebowe znaczenie, chryzantemy na grób, parzysta liczba kwiatów na grób'
            heading={<>Jakie kwiaty na cmentarz?<em> Wieńce, wiązanki i ich znaczenie</em></>}
            lead='Wieniec czy wiązanka, lilie czy chryzantemy, biel czy czerwień? Podpowiadamy, jakie kwiaty wybrać na pogrzeb i na grób, co oznaczają i jak dopasować je do pory roku.'
            actions={
                <>
                    <HashLink smooth to='/jakie-kwiaty-na-cmentarz#kwiaty' className='guide_btn guide_btn--light'>
                        Znaczenie kwiatów
                    </HashLink>
                    <HashLink smooth to='/jakie-kwiaty-na-cmentarz#pory-roku' className='guide_btn guide_btn--ghost'>
                        Kwiaty na grób
                    </HashLink>
                </>
            }
            faq={FAQ}
        >
            <section className='guide_section'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Rodzaje kompozycji</span>
                    <h2 className='guide_title'>Wieniec, wiązanka czy stroik?</h2>
                    <p className='guide_lead'>
                        Kwiaty to ostatni gest wobec zmarłego i wyraz wsparcia dla rodziny. Rodzaj kompozycji zależy przede
                        wszystkim od tego, jak blisko było się z osobą zmarłą.
                    </p>
                    <GuidePhoto photo={PHOTOS.wreath} />
                    <div className='guide_cards guide_cards--compact'>
                        {ARRANGEMENTS.map((item) => (
                            <article key={item.title} className='guide_card'>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className='guide_section guide_section--soft' id='kwiaty'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Symbolika</span>
                    <h2 className='guide_title'>Kwiaty pogrzebowe i ich znaczenie</h2>
                    <GuidePhoto photo={PHOTOS.lilies} />
                    <div className='guide_cards'>
                        {FLOWERS.map((flower) => (
                            <article key={flower.title} className='guide_card'>
                                <h3>{flower.title}</h3>
                                <p>{flower.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Kolory</span>
                    <h2 className='guide_title'>Jaki kolor kwiatów na pogrzeb?</h2>
                    <GuidePhoto photo={PHOTOS.chrysanthemums} />
                    <ul className='guide_checklist'>
                        {COLORS.map((color) => (
                            <li key={color.title}>
                                <strong>{color.title}</strong>
                                <span>{color.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className='guide_section guide_section--soft' id='pory-roku'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Kwiaty na grób</span>
                    <h2 className='guide_title'>Jakie kwiaty na cmentarz o każdej porze roku?</h2>
                    <p className='guide_lead'>
                        Na grób, który odwiedzamy po pogrzebie, lepiej sprawdzają się rośliny w doniczkach niż kwiaty cięte.
                        Wybieraj gatunki, które dobrze znoszą pogodę danej pory roku.
                    </p>
                    <GuidePhoto photo={PHOTOS.cemetery} />
                    <div className='guide_cards guide_cards--compact'>
                        {SEASONS.map((season) => (
                            <article key={season.title} className='guide_card'>
                                <h3>{season.title}</h3>
                                <p>{season.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Wskazówki</span>
                    <h2 className='guide_title'>O czym pamiętać, kupując kwiaty na pogrzeb</h2>
                    <ul className='guide_checklist'>
                        {TIPS.map((tip) => (
                            <li key={tip.title}>
                                <strong>{tip.title}</strong>
                                <span>{tip.text}</span>
                            </li>
                        ))}
                    </ul>
                    <p className='guide_note'>
                        Nie wiesz, co napisać na szarfie? Zobacz{' '}
                        <Link to='/napisy-na-szarfy'>gotowe napisy na szarfy pogrzebowe</Link> - dla mamy, taty, babci,
                        przyjaciela i od współpracowników.
                    </p>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <div className='guide_callout'>
                        <div>
                            <h2>
                                Prowadzisz kwiaciarnię?<em> Szarfy do wieńców w kilka minut</em>
                            </h2>
                            <p>
                                W Nekrodruku przygotujesz szarfę z gotowym napisem, symbolem i obramowaniem, a plik PDF ma
                                dokładnie wymiary szarfy. Zobacz, jak wygląda{' '}
                                <Link to='/drukowanie-szarf-pogrzebowych'>drukowanie szarf pogrzebowych</Link>.
                            </p>
                        </div>
                        <div className='guide_actions'>
                            <Link to='/register' className='guide_btn guide_btn--light'>
                                Załóż konto
                            </Link>
                            <HashLink smooth to='/#Contact' className='guide_btn guide_btn--ghost'>
                                Napisz do nas
                            </HashLink>
                        </div>
                    </div>
                </div>
            </section>
        </GuideLayout>
    );
}
